import { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button, Modal } from '@fiado/react-kit';
import { handleApiError, resolveErrorMessage } from '../../api/errorMapping';
import { ApiError } from '../../api/errors';
import { apiUserIpAllowlistError, parseIpAllowlist } from '../../lib/apiUserIpAllowlist';
import { IpAllowlistField } from './IpAllowlistField';
import type { UpdateApiUserAccessRequestLocal } from '../../api/types/TenantLocal';

const schema = z.object({
    ipAllowlist: z.string().superRefine((raw, ctx) => {
        const message = apiUserIpAllowlistError(raw);
        if (message) ctx.addIssue({ code: z.ZodIssueCode.custom, message });
    }),
});

type FormValues = z.infer<typeof schema>;

interface Props {
    /** Guarda el acceso con el binding del flujo (tenant o drill-down de plataforma). */
    save: (body: UpdateApiUserAccessRequestLocal) => Promise<unknown>;
    systemName: string;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    initialIpAllowlist?: string[];
    onSaved: () => void;
}

/** Edita las IPs desde las que un sistema (API) puede hacer login. La lista nunca queda vacía. */
export function EditApiAccessDialog({
    save,
    systemName,
    open,
    onOpenChange,
    initialIpAllowlist,
    onSaved,
}: Props) {
    const initialText = (initialIpAllowlist ?? []).join('\n');
    const form = useForm<FormValues>({
        resolver: zodResolver(schema),
        defaultValues: { ipAllowlist: initialText },
    });
    const ipAllowlist = useWatch({ control: form.control, name: 'ipAllowlist' });

    useEffect(() => {
        if (open) form.reset({ ipAllowlist: initialText });
    }, [open, initialText, form]);

    const saveMut = useMutation({
        mutationFn: (values: FormValues) =>
            save({ ipAllowlist: parseIpAllowlist(values.ipAllowlist) }),
        onSuccess: () => {
            toast.success('IPs permitidas actualizadas. Aplican desde el siguiente login.');
            onOpenChange(false);
            onSaved();
        },
        onError: (err) => {
            if (handleApiError(err) === 'INLINE' && err instanceof ApiError) {
                form.setError('ipAllowlist', { message: resolveErrorMessage(err) });
            }
        },
    });

    return (
        <Modal
            isOpen={open}
            onClose={() => {
                onOpenChange(false);
            }}
            title={`IPs permitidas de ${systemName}`}
            size="md"
        >
            <form
                className="space-y-4"
                onSubmit={(e) => {
                    void form.handleSubmit((values) => {
                        saveMut.mutate(values);
                    })(e);
                }}
            >
                <IpAllowlistField
                    id="edit-api-ip-allowlist"
                    value={ipAllowlist}
                    registration={form.register('ipAllowlist')}
                    error={form.formState.errors.ipAllowlist?.message}
                />
                <div className="flex justify-end gap-2">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                            onOpenChange(false);
                        }}
                    >
                        Cancelar
                    </Button>
                    <Button type="submit" isLoading={saveMut.isPending}>
                        Guardar
                    </Button>
                </div>
            </form>
        </Modal>
    );
}
